import React, { useEffect, useRef, useState } from 'react'
import ReactDOM from 'react-dom'
import styled from 'styled-components'
import moment from 'moment'
import Calendar from 'react-calendar'

interface SnoozeOption {
    label: string
    time: moment.Moment
}

const getSnoozeOptions = (): SnoozeOption[] => {
    const now = moment()
    const options: SnoozeOption[] = []

    if (now.hour() < 23) {
        let laterToday: moment.Moment
        if (now.hour() < 17) {
            laterToday = now.clone().startOf('day').hour(18).minute(0).second(0).millisecond(0)
        } else {
            const base = now.clone().add(1, 'hour')
            const mins = base.minute()
            if (mins > 0 && mins <= 30) base.minute(30).second(0).millisecond(0)
            else if (mins > 30) base.add(1, 'hour').minute(0).second(0).millisecond(0)
            laterToday = base
        }
        if (laterToday.isSame(now, 'day')) {
            options.push({ label: 'Later today', time: laterToday })
        }
    }

    const tomorrow = now.clone().add(1, 'day').startOf('day').hour(8).minute(0).second(0).millisecond(0)
    options.push({ label: 'Tomorrow', time: tomorrow })

    const thisThursday = now.clone().isoWeekday(4).startOf('day').hour(8).minute(0).second(0).millisecond(0)
    if (thisThursday.isAfter(tomorrow, 'day')) {
        options.push({ label: 'Later this week', time: thisThursday })
    }

    let weekend: moment.Moment
    if (now.isoWeekday() === 6) {
        weekend = now.clone().add(1, 'day').startOf('day').hour(8).minute(0).second(0).millisecond(0)
    } else {
        weekend = now.clone().isoWeekday(6).startOf('day').hour(8).minute(0).second(0).millisecond(0)
        if (!weekend.isAfter(now, 'day')) weekend.add(7, 'days')
    }
    if (!weekend.isSame(tomorrow, 'day')) {
        options.push({ label: 'This weekend', time: weekend })
    }

    const nextMonday = now.clone().startOf('isoWeek').add(7, 'days').hour(8).minute(0).second(0).millisecond(0)
    options.push({ label: 'Next week', time: nextMonday })

    return options
}

const formatSnoozeTime = (time: moment.Moment): string => time.format('ddd, HH:mm')


// Heights used only for the "right" placement (sidebar) and the "flip below" threshold.
const MENU_HEIGHT_LIST = 300
const MENU_HEIGHT_PICKER = 430
const MENU_WIDTH_LIST = 260
const MENU_WIDTH_PICKER = 460
const MARGIN = 8

const computeMenuStyle = (
    anchor: { top: number; right?: number; left?: number },
    placement: 'up' | 'right',
    isPickerOpen: boolean,
): React.CSSProperties => {
    const vw = typeof window !== 'undefined' ? window.innerWidth : 1200
    const vh = typeof window !== 'undefined' ? window.innerHeight : 800
    const menuW = isPickerOpen ? MENU_WIDTH_PICKER : MENU_WIDTH_LIST
    const menuH = isPickerOpen ? MENU_HEIGHT_PICKER : MENU_HEIGHT_LIST

    if (placement === 'right') {
        const top = Math.max(MARGIN, Math.min(anchor.top, vh - menuH - MARGIN))
        const left = Math.max(MARGIN, (anchor.left ?? 0) + 6)
        return { top, left }
    }

    const right = Math.max(MARGIN, Math.min(anchor.right ?? 0, vw - menuW - MARGIN))

    // Use `bottom` positioning so the menu snaps flush against the button regardless
    // of actual rendered height (avoids the gap caused by height over-estimation).
    // However, only use "above" if there's actually enough vertical room.
    if (anchor.top >= menuH + MARGIN) {
        // Enough room above — snap flush to button using bottom positioning
        return { bottom: vh - anchor.top + 4, right }
    }
    // Not enough room above — place below the button, clamped so it doesn't clip the viewport bottom
    const topBelow = Math.min(anchor.top + 26, vh - menuH - MARGIN)
    return { top: Math.max(MARGIN, topBelow), right }
}

interface ISnoozeMenuProps {
    anchor: { top: number; right?: number; left?: number }
    placement?: 'up' | 'right'
    onSnooze: (snoozedUntil: number) => void
    onClose: () => void
}

const DATE_INPUT_FORMATS = ['MMM D, YYYY', 'MM/DD/YYYY', 'M/D/YYYY', 'YYYY-MM-DD', 'D MMM YYYY', 'MMM D YYYY', 'D. M. YYYY', 'D.M.YYYY']
const TIME_INPUT_FORMATS = ['H:mm', 'H:m', 'HH:mm', 'H']

const parseDate = (str: string): Date | null => {
    const m = moment(str, DATE_INPUT_FORMATS, true)
    return m.isValid() ? m.toDate() : null
}
const parseTime = (str: string): { hour: number; minute: number } | null => {
    const m = moment(str.trim(), TIME_INPUT_FORMATS, true)
    return m.isValid() ? { hour: m.hour(), minute: m.minute() } : null
}

const SnoozeMenu: React.FC<ISnoozeMenuProps> = ({ anchor, placement = 'up', onSnooze, onClose }) => {
    const ref = useRef<HTMLDivElement>(null)
    const options = getSnoozeOptions()
    const [isPickerOpen, setIsPickerOpen] = useState(false)
    const [pickedDate, setPickedDate] = useState<Date>(() => moment().add(1, 'day').toDate())
    const [pickedHour, setPickedHour] = useState(() => moment().add(1, 'hour').startOf('hour').hour())
    const [pickedMinute, setPickedMinute] = useState(0)

    const [dateText, setDateText] = useState(() => moment().add(1, 'day').format('MMM D, YYYY'))
    const [timeText, setTimeText] = useState(() => moment().add(1, 'hour').startOf('hour').format('H:mm'))
    const [dateValid, setDateValid] = useState(true)
    const [timeValid, setTimeValid] = useState(true)
    // calendarKey forces react-calendar to re-sync its displayed month when date text is edited
    const [calendarKey, setCalendarKey] = useState(0)

    const pickedMoment = moment(pickedDate).startOf('day').hour(pickedHour).minute(pickedMinute)
    const isInPast = pickedMoment.isSameOrBefore(moment())

    const menuStyle = computeMenuStyle(anchor, placement, isPickerOpen)

    useEffect(() => {
        const handleClickOutside = (e: MouseEvent) => {
            if (ref.current && !ref.current.contains(e.target as Node)) onClose()
        }
        document.addEventListener('mousedown', handleClickOutside)
        return () => document.removeEventListener('mousedown', handleClickOutside)
    }, [onClose])

    const handleCalendarChange = (value: any) => {
        const date = Array.isArray(value) ? value[1] ?? value[0] : value
        if (date) {
            setPickedDate(date)
            setDateText(moment(date).format('MMM D, YYYY'))
            setDateValid(true)
        }
    }

    const handleDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const val = e.target.value
        setDateText(val)
        const parsed = parseDate(val)
        if (parsed) {
            setPickedDate(parsed)
            setDateValid(true)
            setCalendarKey(k => k + 1)
        } else {
            setDateValid(false)
        }
    }

    const handleTimeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const val = e.target.value
        setTimeText(val)
        const parsed = parseTime(val)
        if (parsed) {
            setPickedHour(parsed.hour)
            setPickedMinute(parsed.minute)
            setTimeValid(true)
        } else {
            setTimeValid(false)
        }
    }

    const handleConfirm = () => {
        if (!dateValid || !timeValid || isInPast) return
        onSnooze(pickedMoment.valueOf())
        onClose()
    }

    return ReactDOM.createPortal(
        <MenuContainer
            ref={ref}
            style={menuStyle}
            $pickerOpen={isPickerOpen}
            onClick={(e) => e.stopPropagation()}
        >
            <MenuHeader>
                {isPickerOpen ? (
                    <BackButton onClick={() => setIsPickerOpen(false)}>
                        <ChevronLeftSvg />
                    </BackButton>
                ) : (
                    <ClockSvg />
                )}
                {isPickerOpen ? 'Pick date & time' : 'Snooze until...'}
            </MenuHeader>

            {!isPickerOpen ? (
                <>
                    {options.map((opt) => (
                        <MenuItem key={opt.label} onClick={() => { onSnooze(opt.time.valueOf()); onClose() }}>
                            <OptionLabel>{opt.label}</OptionLabel>
                            <OptionTime>{formatSnoozeTime(opt.time)}</OptionTime>
                        </MenuItem>
                    ))}
                    <Divider />
                    <PickDateItem onClick={() => setIsPickerOpen(true)}>
                        <CalendarSvg />
                        <span>Pick date &amp; time</span>
                    </PickDateItem>
                </>
            ) : (
                <>
                    <PickerMain>
                        <StyledCalendar
                            key={calendarKey}
                            value={pickedDate}
                            onChange={handleCalendarChange}
                            showNeighboringMonth={false}
                            minDate={moment().toDate()}
                            returnValue="range"
                            minDetail="month"
                            formatMonthYear={(_locale: any, date: Date) => moment(date).format('MMMM YYYY')}
                            defaultActiveStartDate={pickedDate}
                            locale="en-US"
                        />
                        <PickerSidebar>
                            <InputBox $invalid={!dateValid}>
                                <InputBoxLabel>Date</InputBoxLabel>
                                <InputBoxField
                                    value={dateText}
                                    onChange={handleDateChange}
                                    spellCheck={false}
                                />
                            </InputBox>
                            <InputBoxError $visible={!dateValid}>Invalid date</InputBoxError>
                            <InputBox $invalid={!timeValid}>
                                <InputBoxLabel>Time</InputBoxLabel>
                                <InputBoxField
                                    value={timeText}
                                    onChange={handleTimeChange}
                                    spellCheck={false}
                                    placeholder="H:MM"
                                />
                            </InputBox>
                            <InputBoxError $visible={!timeValid || (dateValid && timeValid && isInPast)}>
                                {!timeValid ? 'Invalid time' : 'Time is in the past'}
                            </InputBoxError>
                        </PickerSidebar>
                    </PickerMain>
                    <PickerFooter>
                        <CancelBtn onClick={onClose}>Cancel</CancelBtn>
                        <SaveBtn disabled={!dateValid || !timeValid || isInPast} onClick={handleConfirm}>Save</SaveBtn>
                    </PickerFooter>
                </>
            )}
        </MenuContainer>,
        document.body
    )
}

// ─── Icons ────────────────────────────────────────────────────────────────────

const ClockSvg = () => (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#6467f2" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" />
    </svg>
)
const CalendarSvg = () => (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#6467f2" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
        <line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" /><line x1="3" y1="10" x2="21" y2="10" />
    </svg>
)
const ChevronLeftSvg = () => (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#6467f2" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="15 18 9 12 15 6" />
    </svg>
)

// ─── Styles ───────────────────────────────────────────────────────────────────

const MenuContainer = styled.div<{ $pickerOpen: boolean }>`
    position: fixed;
    z-index: 99999;
    background: #ffffff;
    border: 1px solid #e2e8f0;
    border-radius: 10px;
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
    width: ${(p) => p.$pickerOpen ? '460px' : '260px'};
    padding: 4px 0;
    font-family: 'Nunito Sans', sans-serif;
`

const MenuHeader = styled.div`
    display: flex;
    align-items: center;
    gap: 7px;
    padding: 11px 16px 10px;
    font-size: 13px;
    font-weight: 700;
    color: #333951;
    border-bottom: 1px solid #f1f5f9;
    margin-bottom: 2px;
`

const BackButton = styled.button`
    display: flex;
    align-items: center;
    background: none;
    border: none;
    padding: 0;
    cursor: pointer;
    line-height: 0;
`

const MenuItem = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 9px 16px;
    font-size: 13px;
    color: #333951;
    cursor: pointer;
    &:hover { background-color: #f8fafc; }
`

const OptionLabel = styled.span`flex: 1;`
const OptionTime = styled.span`
    font-size: 12px;
    color: #94a3b8;
    white-space: nowrap;
`

const Divider = styled.div`
    height: 1px;
    background: #f1f5f9;
    margin: 4px 0;
`

const PickDateItem = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 9px 16px;
    font-size: 13px;
    color: #6467f2;
    cursor: pointer;
    &:hover { background-color: #f8fafc; }
`

// ─── Picker layout ────────────────────────────────────────────────────────────

const PickerMain = styled.div`
    display: flex;
    gap: 12px;
    padding: 12px 16px 8px;
    align-items: flex-start;
`

const PickerSidebar = styled.div`
    display: flex;
    flex-direction: column;
    gap: 10px;
    flex: 1;
    min-width: 0;
`

const InputBox = styled.div<{ $invalid?: boolean }>`
    border: 1.5px solid ${(p) => p.$invalid ? '#ef4444' : '#e2e8f0'};
    border-radius: 8px;
    padding: 8px 12px;
    transition: border-color 0.15s;
    &:focus-within { border-color: ${(p) => p.$invalid ? '#ef4444' : '#6467f2'}; }
`

const InputBoxLabel = styled.div`
    font-size: 10px;
    font-weight: 700;
    color: #94a3b8;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    margin-bottom: 3px;
`

const InputBoxField = styled.input`
    display: block;
    width: 100%;
    border: none;
    outline: none;
    padding: 0;
    font-family: 'Nunito Sans', sans-serif;
    font-size: 13px;
    font-weight: 600;
    color: #333951;
    background: transparent;
`

const InputBoxError = styled.div<{ $visible?: boolean }>`
    font-size: 11px;
    color: #ef4444;
    height: 16px;
    line-height: 16px;
    margin-top: 2px;
    margin-bottom: 2px;
    visibility: ${(p) => p.$visible ? 'visible' : 'hidden'};
`

const PickerFooter = styled.div`
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 8px;
    padding: 8px 16px 12px;
    border-top: 1px solid #f1f5f9;
    margin-top: 4px;
`

const CancelBtn = styled.button`
    background: none;
    border: none;
    font-family: 'Nunito Sans', sans-serif;
    font-size: 13px;
    font-weight: 700;
    color: #6467f2;
    cursor: pointer;
    padding: 8px 12px;
    border-radius: 20px;
    &:hover { background: #f1f5f9; }
`

const SaveBtn = styled.button<{ disabled: boolean }>`
    font-family: 'Nunito Sans', sans-serif;
    font-size: 13px;
    font-weight: 700;
    color: #ffffff;
    background: ${(p) => p.disabled ? '#c7c8f9' : '#6467f2'};
    border: none;
    border-radius: 20px;
    padding: 8px 20px;
    cursor: ${(p) => p.disabled ? 'default' : 'pointer'};
    transition: background 0.15s;
    &:hover:not(:disabled) { background: #5254cc; }
`

const StyledCalendar = styled(Calendar)`
    width: 228px;
    flex-shrink: 0;
    font-family: 'Nunito Sans', sans-serif;
    font-size: 13px;

    & abbr { text-decoration: none; }

    & button {
        border: none;
        background: transparent;
        font-family: 'Nunito Sans', sans-serif;
        &:disabled { color: #cbd5e1; }
        &:not(:disabled) { color: #333951; cursor: pointer; }
    }

    & .react-calendar__navigation {
        display: flex;
        align-items: center;
        height: 28px;
        margin-bottom: 12px;
    }

    & .react-calendar__navigation__label {
        font-size: 13px;
        font-weight: 700;
        color: #333951;
    }

    & .react-calendar__navigation__arrow {
        font-size: 18px;
        color: #8c96ad;
        display: flex;
        align-items: center;
        justify-content: center;
        width: 28px;
        height: 28px;
        border-radius: 6px;
        &:hover:not(:disabled) { background: #f1f5f9; }
    }

    & .react-calendar__month-view__weekdays {
        margin-bottom: 4px;
    }

    & .react-calendar__month-view__weekdays__weekday {
        text-align: center;
        font-size: 11px;
        font-weight: 700;
        color: #94a3b8;
        padding: 0;
    }

    & .react-calendar__tile {
        height: 32px;
        width: 32px;
        border-radius: 50%;
        font-size: 13px;
        display: flex;
        align-items: center;
        justify-content: center;
        padding: 0;
        &:hover:not(:disabled) { background: #f1f5f9; color: #333951; }
    }

    & .react-calendar__tile--now {
        background: transparent;
        border: 1.5px solid #6467f2 !important;
        color: #6467f2 !important;
    }

    & .react-calendar__tile--active,
    & .react-calendar__tile--rangeStart,
    & .react-calendar__tile--rangeEnd {
        background: #6467f2 !important;
        color: #ffffff !important;
        border: none !important;
        border-radius: 50%;
        &:hover { background: #5254cc !important; }
    }
`

export default SnoozeMenu
